import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Sparkles, X, Send, ArrowRight, Zap } from 'lucide-react';

export default function AIInstantButton() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [question, setQuestion] = useState('');

  const handleQuickQuestion = () => {
    if (question.trim()) {
      // Navigate to chat with the question
      navigate('/chat', { state: { initialMessage: question } });
      setIsOpen(false);
      setQuestion('');
      toast.success('Question envoyée à l\'IA');
    } else {
      toast.error('Veuillez saisir une question');
    }
  };

  const handleSuggestionClick = (q) => {
    setQuestion(q);
  };

  const handleDirectNavigate = (path, label) => {
    navigate(path);
    setIsOpen(false);
    toast.success(`Accès à ${label}`);
  };

  const suggestions = [
    "Posologie adrénaline ACR adulte?",
    "Protocole AVC thrombolyse critères?",
    "Critères SCA ST+ et prise en charge?",
    "Intubation difficile algorithme?",
    "Score Red Flags trauma sévère?"
  ];

  const quickLinks = [
    { label: 'SCA ST+', path: '/sca-st-plus', color: 'from-red-500 to-pink-500' },
    { label: 'AVC Expert', path: '/avc-expert', color: 'from-purple-500 to-indigo-500' },
    { label: 'Trauma Sévère', path: '/trauma-severe', color: 'from-orange-500 to-red-500' },
    { label: 'Intubation', path: '/intubation', color: 'from-blue-500 to-cyan-500' },
    { label: 'Médicaments', path: '/medications', color: 'from-green-500 to-emerald-500' },
    { label: 'Protocoles', path: '/protocols', color: 'from-cyan-500 to-teal-500' }
  ];

  return (
    <>
      {/* Floating Button - Positioned at TOP RIGHT */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-4 right-4 md:top-6 md:right-6 z-50 w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-purple-600 flex items-center justify-center shadow-lg hover:shadow-xl transition-all hover:scale-110 glow-purple group"
        aria-label="AI Instantané"
        data-testid="ai-instant-button"
      >
        {isOpen ? (
          <X className="w-5 h-5 md:w-6 md:h-6 text-white" />
        ) : (
          <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-white animate-pulse group-hover:animate-spin" />
        )}
      </button>

      {/* Quick Panel - Positioned BELOW the button */}
      {isOpen && (
        <div 
          className="fixed top-20 right-4 md:top-24 md:right-6 z-50 w-[calc(100vw-2rem)] md:w-[420px] max-h-[75vh] bg-[hsl(var(--bg-elev))] border border-purple-400/30 rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-top-2 fade-in duration-200"
          data-testid="ai-instant-panel"
        >
          {/* Header */}
          <div className="p-4 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-purple-500/20 border-b border-purple-400/30">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded-lg bg-purple-400/20 glow-purple">
                <Zap className="w-5 h-5 text-purple-300" />
              </div>
              <h3 className="font-semibold text-[hsl(var(--text))] text-lg">AI Instantané</h3>
            </div>
            <p className="text-xs text-[hsl(var(--text-dim))]">
              Posez votre question médicale ou accédez rapidement à un outil
            </p>
          </div>

          {/* Input Section */}
          <div className="p-4 border-b border-[hsl(var(--border))]">
            <div className="flex gap-2">
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleQuickQuestion()}
                placeholder="Ex: Posologie adrénaline anaphylaxie?"
                className="flex-1 px-4 py-2.5 text-sm bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded-lg text-[hsl(var(--text))] placeholder:text-[hsl(var(--text-dim))] focus:outline-none focus:ring-2 focus:ring-purple-400/50 focus:border-purple-400"
                data-testid="ai-instant-input"
              />
              <Button
                onClick={handleQuickQuestion}
                size="sm"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-4"
                data-testid="ai-instant-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Suggestions */}
          <div className="p-4 max-h-48 overflow-y-auto border-b border-[hsl(var(--border))]">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <p className="text-xs font-semibold text-purple-300">Suggestions rapides</p>
            </div>
            <div className="space-y-2">
              {suggestions.map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSuggestionClick(s)}
                  className="w-full text-left p-2.5 rounded-lg bg-[hsl(var(--bg))] hover:bg-purple-500/10 border border-[hsl(var(--border))] hover:border-purple-400/40 transition-all text-xs text-[hsl(var(--text))] group"
                  data-testid={`ai-instant-suggestion-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{s}</span>
                    <ArrowRight className="w-3 h-3 text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="p-4 bg-[hsl(var(--bg))]/50">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-cyan-400" />
              <p className="text-xs font-semibold text-cyan-300">Accès rapide</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {quickLinks.map((link, idx) => (
                <button
                  key={idx}
                  onClick={() => handleDirectNavigate(link.path, link.label)}
                  className={`px-3 py-2.5 text-xs font-medium rounded-lg bg-gradient-to-br ${link.color} bg-opacity-10 hover:bg-opacity-20 border border-white/10 hover:border-white/20 text-white transition-all hover:scale-105 shadow-sm hover:shadow-md`}
                  data-testid={`ai-instant-link-${idx}`}
                >
                  {link.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .glow-purple {
          box-shadow: 0 0 20px rgba(168, 85, 247, 0.4), 0 0 40px rgba(168, 85, 247, 0.2);
        }
      `}</style>
    </>
  );
}
